import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { DownloadIcon } from "@chakra-ui/icons";
import {
	Badge,
	Button,
	ButtonGroup,
	Card,
	CardBody,
	CardHeader,
	Checkbox,
	FormControl,
	FormLabel,
	HStack,
	IconButton,
	Select,
	Stack,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tooltip,
	Tr,
	VStack,
	Wrap,
	WrapItem,
	useColorModeValue,
} from "@chakra-ui/react";
import moment from "moment";

import Loader from "../../Components/Loader/Loader";
import DocumentsTableRow from "../../Components/TableRows/DocumentsTableRow";
import { selectActiveProperties } from "../../redux/user/userSlice";
import {
	DEFAULT_WORKLIST_WINDOW_DAYS,
	WORKLIST_WINDOWS,
	buildWorklist,
	downloadCsv,
	flattenAgencyDocuments,
	isAgencyDocumentType,
	worklistToCsv,
} from "../../utils/agencyDocuments";
import { API } from "../../utils/http";
import { documentTypes, fullDateFormat } from "../../utils/types";
import { removeAccents } from "../../utils/utils";
import { getPropertyAddress } from "../../utils/verifier";
import SearchBox from "../Properties/SearchBox";

const MODE_WORKLIST = "worklist";
const MODE_BROWSE = "browse";

function statusColorScheme(status) {
	if (status === "expired") return "red";
	if (status === "expiring") return "orange";
	return "gray";
}

function matchesBrowseDocument(document, documentType) {
	if (documentType) return document.type === documentType;
	return isAgencyDocumentType(document.type);
}

export default function Documents() {
	const storeProperties = useSelector(selectActiveProperties);
	const properties = useMemo(
		() => [...(storeProperties || [])].sort((a, b) => a.owner.name.localeCompare(b.owner.name, "ro", { sensitivity: "base" })),
		[storeProperties],
	);
	const [mode, setMode] = useState(MODE_WORKLIST);
	const [query, setQuery] = useState("");
	const [loading, setLoading] = useState(true);
	const [documentType, setDocumentType] = useState(documentTypes[0]?.id || "administrationContract");
	const [status, setStatus] = useState("all");
	const [windowDays, setWindowDays] = useState(DEFAULT_WORKLIST_WINDOW_DAYS);
	const [activeOnly, setActiveOnly] = useState(true);

	const { t } = useTranslation();
	const navigate = useNavigate();
	const hoverColor = useColorModeValue("gray.100", "gray.700");
	const secondaryText = useColorModeValue("gray.600", "gray.400");

	useEffect(() => {
		setLoading(false);
	}, []);

	const flatRows = useMemo(() => flattenAgencyDocuments(properties), [properties]);
	const worklist = useMemo(
		() =>
			buildWorklist(flatRows, {
				status,
				windowDays,
				type: documentType,
				activeOnly,
				query,
			}),
		[flatRows, status, windowDays, documentType, activeOnly, query],
	);

	const browseGroups = useMemo(() => {
		const normalizedQuery = removeAccents(String(query || "").trim().toLowerCase());
		return properties
			.filter((property) => {
				if (!normalizedQuery) return true;
				const address = removeAccents(getPropertyAddress(property).toLowerCase());
				const ownerName = removeAccents(String(property.owner?.name || "").toLowerCase());
				return ownerName.includes(normalizedQuery) || address.includes(normalizedQuery);
			})
			.map((property) => ({
				property,
				documents: (property.documents || []).filter((document) => matchesBrowseDocument(document, documentType)),
			}))
			.filter((group) => group.documents.length > 0);
	}, [properties, query, documentType]);

	const goToProperty = (id) => {
		navigate(`/properties/view/${id}?tab=documents`);
	};

	const viewDocument = (event, documentId, propertyId, documentName) => {
		event.stopPropagation();
		API.put("get-document", { documentId, propertyId, documentName }).then((response) => {
			window.open(response.data.url, "_blank");
		});
	};

	const handleExportWorklist = () => {
		const csv = worklistToCsv(worklist.rows);
		const filename = `expiring-documents-${moment().format("YYYY-MM-DD")}.csv`;
		downloadCsv(csv, filename);
	};

	const handleExportBrowse = () => {
		const allDocuments = browseGroups
			.flatMap(({ property, documents }) =>
				documents.map((document) => ({
					...document,
					owner: property.owner.name,
					address: getPropertyAddress(property),
				})),
			)
			.sort((a, b) => moment(a.startingDate).unix() - moment(b.startingDate).unix());

		const header = ["startingDate", "owner", "address", "type", "number", "expirationDate"];
		const replacer = (key, value) => (value === null ? "" : value);
		let csvData = allDocuments.map((row) => header.map((fieldName) => JSON.stringify(row[fieldName], replacer)).join(","));
		csvData.unshift(header.join(","));
		csvData = csvData.join("\r\n");
		downloadCsv(csvData, "exported_documents.csv");
	};

	const renderModeToggle = () => (
		<ButtonGroup size="sm" isAttached variant="outline">
			<Button
				colorScheme={mode === MODE_WORKLIST ? "teal" : undefined}
				variant={mode === MODE_WORKLIST ? "solid" : "outline"}
				onClick={() => setMode(MODE_WORKLIST)}
			>
				{t("documents.modeWorklist")}
			</Button>
			<Button
				colorScheme={mode === MODE_BROWSE ? "teal" : undefined}
				variant={mode === MODE_BROWSE ? "solid" : "outline"}
				onClick={() => setMode(MODE_BROWSE)}
			>
				{t("documents.modeBrowse")}
			</Button>
		</ButtonGroup>
	);

	const renderTypeSelect = () => (
		<FormControl display="flex" alignItems="center">
			<FormLabel mb="0" whiteSpace="nowrap">
				{t("documents.type")}:
			</FormLabel>
			<Select width="fit-content" value={documentType} onChange={(event) => setDocumentType(event.target.value)}>
				<option value="">{t("documents.typeAll")}</option>
				{documentTypes.map((entry) => (
					<option key={entry.id} value={entry.id}>
						{entry.label}
					</option>
				))}
			</Select>
		</FormControl>
	);

	return (
		<Stack direction="column" spacing={2}>
			<Card overflowX={{ sm: "scroll", xl: "hidden" }}>
				<CardHeader fontSize="xl" fontWeight="bold">
					<Stack direction="column" spacing={3}>
						<HStack justify="space-between" align="center" flexWrap="wrap" spacing={3}>
							<Text>{t("documents.documents")}</Text>
							{renderModeToggle()}
						</HStack>
						<Wrap spacing={3} align="center">
							<WrapItem>
								<SearchBox
									handleSearch={setQuery}
									searchOptions={[
										{ label: "Address", description: "Search by address" },
										{ label: "Owner", description: "Search by owner" },
									]}
								/>
							</WrapItem>
							<WrapItem>{renderTypeSelect()}</WrapItem>
							{mode === MODE_WORKLIST && (
								<>
									<WrapItem>
										<FormControl display="flex" alignItems="center">
											<FormLabel mb="0" whiteSpace="nowrap">
												{t("documents.status")}:
											</FormLabel>
											<Select width="fit-content" value={status} onChange={(event) => setStatus(event.target.value)}>
												<option value="all">{t("documents.statusAllWithExpiry")}</option>
												<option value="expired">{t("documents.statusExpired")}</option>
												<option value="expiring">{t("documents.statusExpiring")}</option>
											</Select>
										</FormControl>
									</WrapItem>
									<WrapItem>
										<FormControl display="flex" alignItems="center">
											<FormLabel mb="0" whiteSpace="nowrap">
												{t("documents.window")}:
											</FormLabel>
											<Select
												width="fit-content"
												value={windowDays}
												onChange={(event) => setWindowDays(Number(event.target.value))}
											>
												{WORKLIST_WINDOWS.map((days) => (
													<option key={days} value={days}>
														{t("documents.windowDays", { days })}
													</option>
												))}
											</Select>
										</FormControl>
									</WrapItem>
									<WrapItem>
										<Checkbox isChecked={activeOnly} onChange={(event) => setActiveOnly(event.target.checked)}>
											{t("documents.activeOnly")}
										</Checkbox>
									</WrapItem>
								</>
							)}
							<WrapItem>
								<Button onClick={mode === MODE_WORKLIST ? handleExportWorklist : handleExportBrowse}>
									{t("documents.exportAsCsv")}
								</Button>
							</WrapItem>
						</Wrap>
						{mode === MODE_WORKLIST && (
							<Text fontSize="sm" fontWeight="normal" color="gray.600">
								{t("documents.worklistSummary", {
									total: worklist.total,
									expired: worklist.expiredCount,
									expiring: worklist.expiringCount,
								})}
							</Text>
						)}
					</Stack>
				</CardHeader>
				<CardBody overflowX="auto">
					{loading ? (
						<Loader text="Loading Outstanding..." />
					) : mode === MODE_WORKLIST ? (
						<Table variant="simple" size="sm">
							<Thead>
								<Tr whiteSpace="nowrap">
									<Th pl="0px">{t("table.expirationDate")}</Th>
									<Th>{t("documents.daysLeft")}</Th>
									<Th>{t("table.type")}</Th>
									<Th>{t("documents.property")}</Th>
									<Th>{t("documents.status")}</Th>
									<Th w="1%">{t("table.actions")}</Th>
								</Tr>
							</Thead>
							<Tbody>
								{worklist.rows.length === 0 ? (
									<Tr>
										<Td colSpan={6}>
											<Text color="gray.500">{t("documents.emptyWorklist")}</Text>
										</Td>
									</Tr>
								) : (
									worklist.rows.map((row) => (
										<Tr
											key={`${row.propertyId}-${row.id}`}
											_hover={{ bg: hoverColor, cursor: "pointer" }}
											onClick={() => goToProperty(row.propertyId)}
										>
											<Td pl="0px" whiteSpace="nowrap">
												{row.expirationDate ? moment(row.expirationDate).format(fullDateFormat) : "-"}
											</Td>
											<Td whiteSpace="nowrap">{row.daysUntilExpiry ?? "-"}</Td>
											<Td whiteSpace="nowrap">{t(`types.${row.type}`) || row.type || "-"}</Td>
											<Td maxW={{ base: "14rem", md: "22rem" }}>
												<VStack align="start" spacing={0}>
													<Tooltip label={row.owner} isDisabled={!row.owner} openDelay={400}>
														<Text fontSize="sm" fontWeight="medium" noOfLines={1}>
															{row.owner || "-"}
														</Text>
													</Tooltip>
													<Tooltip label={row.address} isDisabled={!row.address} openDelay={400}>
														<Text fontSize="xs" color={secondaryText} noOfLines={1}>
															{row.address || "-"}
														</Text>
													</Tooltip>
												</VStack>
											</Td>
											<Td>
												<Badge colorScheme={statusColorScheme(row.expiryStatus)}>
													{t(`documents.status${row.expiryStatus === "expired" ? "Expired" : "Expiring"}`)}
												</Badge>
											</Td>
											<Td>
												<Tooltip label={t("documents.viewFile")} openDelay={300}>
													<IconButton
														aria-label={t("documents.viewFile")}
														icon={<DownloadIcon />}
														size="sm"
														variant="ghost"
														isDisabled={!row.name}
														onClick={(event) => viewDocument(event, row.id, row.propertyId, row.name)}
													/>
												</Tooltip>
											</Td>
										</Tr>
									))
								)}
							</Tbody>
						</Table>
					) : (
						<Table variant="simple" size="sm">
							<Thead>
								<Tr whiteSpace="nowrap">
									<Th pl="0px">{t("table.addedDate")}</Th>
									<Th>{t("table.name")}</Th>
									<Th>{t("table.type")}</Th>
									<Th>{t("table.number")}</Th>
									<Th>{t("table.startingDate")}</Th>
									<Th>{t("table.expirationDate")}</Th>
									<Th w="1%">{t("table.actions")}</Th>
								</Tr>
							</Thead>
							<Tbody>
								{browseGroups.length === 0 ? (
									<Tr>
										<Td colSpan={7}>
											<Text color="gray.500">{t("documents.emptyBrowse")}</Text>
										</Td>
									</Tr>
								) : (
									browseGroups.map(({ property, documents }) => {
										const address = getPropertyAddress(property);
										const ownerName = property.owner?.name || "-";
										return (
											<React.Fragment key={property.id}>
												<Tr
													onClick={() => goToProperty(property.id)}
													_hover={{ bg: hoverColor, cursor: "pointer" }}
												>
													<Td pl="0px" colSpan={7} py={3}>
														<VStack align="start" spacing={0} maxW={{ base: "100%", md: "36rem" }}>
															<Tooltip label={ownerName} isDisabled={!ownerName || ownerName === "-"} openDelay={400}>
																<Text fontSize="sm" fontWeight="bold" noOfLines={1}>
																	{ownerName}
																</Text>
															</Tooltip>
															<Tooltip label={address} isDisabled={!address || address === "-"} openDelay={400}>
																<Text fontSize="xs" color={secondaryText} noOfLines={1}>
																	{address}
																</Text>
															</Tooltip>
														</VStack>
													</Td>
												</Tr>
												{documents.map((row) => (
													<DocumentsTableRow
														key={row.id || `${property.id}-${row.number}-${row.type}`}
														row={row}
														propertyId={property.id}
													/>
												))}
											</React.Fragment>
										);
									})
								)}
							</Tbody>
						</Table>
					)}
				</CardBody>
			</Card>
		</Stack>
	);
}
